import { Reducer as CreateSliceReducer } from '@reduxjs/toolkit';

import { AnyRecord } from './createRoutineSlice.types';

export type AddGetterProps<S extends AnyRecord = AnyRecord, N extends string = string> = {
    /** to get data with state key & reducer for `injectReducer` */
    injector: { key: N; reducer: Reducer<S, N> };

    /** to get data with state key & reducer for `combineReducers` */
    keyValuePair: Record<Reducer<S, N>['stateKey'], Reducer<S, N>>; // for `combineReducers`
};
type AddKeyProp<N extends string> = { stateKey: N };

type AddPropsAll<S extends AnyRecord, N extends string> = AddGetterProps<S, N> & AddKeyProp<N>;

type Reducer<S extends AnyRecord, N extends string> = CreateSliceReducer<S> &
    Readonly<AddPropsAll<S, N>>;

const getterDescriptors: Record<keyof AddGetterProps, PropertyDescriptor> = {
    injector: {
        get<S extends AnyRecord, N extends string>(this: Reducer<S, N>) {
            return { key: this.stateKey, reducer: this };
        },
    },

    keyValuePair: {
        get<S extends AnyRecord, N extends string>(this: Reducer<S, N>) {
            return { [this.stateKey]: this };
        },
    },
};

/**
 * A wrapper function over the original `createReducer` of `@reduxjs/toolkit`, created to provide\
 * SINGLE SOURCE OF TRUTH for reducer key - to be reused in `combineReducers` / `injectReducer`;
 */
export default function withKeyProps<State extends AnyRecord, Name extends string>(
    reducer: CreateSliceReducer<State>,
    name: Name,
): Reducer<State, Name> {
    (reducer as unknown as AddKeyProp<Name>).stateKey = name;
    Object.defineProperties(reducer, getterDescriptors);

    return reducer as Reducer<State, Name>;
}
